/**
 * Campaign Resource List frontend behavior.
 *
 * - Filters/search submit → REST request → re-render the card list.
 * - Quick filters apply both keywords and tax filters in one click.
 * - Cart state is in-page (Set of selected resource IDs); Order navigates
 *   to the configured order page with `?{orderQueryArg}=ID,ID,ID`.
 */

const STORAGE_KEY_PREFIX = 'wdg-campaign-resource-list-cart::';

class CampaignResourceListController {
	constructor( root ) {
		this.root = root;
		this.config = parseConfig( root );
		if ( ! this.config ) {
			return;
		}

		this.form = root.querySelector( '[data-crl-form]' );
		this.resultsEl = root.querySelector( '[data-crl-results]' );
		this.paginationEl = root.querySelector( '[data-crl-pagination]' );
		this.loadMoreBtn = root.querySelector( '[data-crl-load-more]' );
		this.pageNavEl = root.querySelector( '[data-crl-page-nav]' );
		this.cartDrawerEl = root.querySelector( '[data-crl-cart]' );
		this.cartBackdropEl = root.querySelector( '[data-crl-cart-backdrop]' );
		this.cartCloseBtn = root.querySelector( '[data-crl-cart-close]' );
		this.cartItemsEl = root.querySelector( '[data-crl-cart-items]' );
		// Count elements: campaign mode has one in the trigger button, marketing mode has one in the cart box.
		this.cartCountEls = Array.from( root.querySelectorAll( '[data-crl-cart-count]' ) );
		this.cartCountEl = this.cartCountEls[ 0 ] || null;
		this.cartOrderBtn = root.querySelector( '[data-crl-cart-order]' );
		this.cartTriggerBtn = root.querySelector( '[data-crl-cart-trigger]' );

		this.cart = restoreCart( this.config.mode );
		this.currentPage = this.config.currentPage || 1;
		this.maxPages = this.config.maxNumPages || 1;
		this.foundPosts = this.config.foundPosts || 0;
		this.suspendSelectChange = false;
		this.isFetching = false;
		this.lastPreviewFocused = null;
		// Multi-term quick filters send more slugs per taxonomy than a native
		// single-value <select> can hold; this carries the full comma-joined
		// slug list per form field name until the user changes that field by hand.
		this.quickFilterTermOverrides = {};

		this.previewModalEl = root.querySelector( '.crl-preview-modal' );
		// New content-calendar-style selectors.
		this.previewModalImg = root.querySelector( '[data-crl-modal-img]' );
		this.previewModalTitle = root.querySelector( '[data-crl-modal-title]' );
		this.previewModalTitleSimple = root.querySelector( '[data-crl-modal-title-simple]' );
		this.previewModalBody = root.querySelector( '[data-crl-modal-social]' );
		this.previewModalPostCopy = root.querySelector( '[data-crl-modal-post-copy]' );
		this.previewModalType = root.querySelector( '[data-crl-modal-type]' );
		this.previewModalShareX = root.querySelector( '[data-crl-modal-share-x]' );
		this.previewModalShareFb = root.querySelector( '[data-crl-modal-share-fb]' );
		this.previewModalDownload = root.querySelector( '[data-crl-modal-download]' );
		this.previewModalDesignHuddle = root.querySelector( '[data-crl-modal-design-huddle]' );
		this.previewModalResourceLink = root.querySelector( '[data-crl-modal-resource-url]' );
		this.previewModalCopyBtn = root.querySelector( '[data-crl-modal-copy-text]' );

		this.bindForm();
		this.bindCustomSelects();
		this.syncDefaultMediaTab();
		this.bindQuickFilters();
		this.applyFiltersFromUrl();
		this.bindCart();
		this.bindPreviewModal();
		this.openInitialSocialResource();
		this.updatePagination();
		this.syncCardSelectedState();
		this.renderCart();
		this.bindSocialMasonry();
	}

	/**
	 * Social mode: row-major masonry over the results grid.
	 *
	 * The grid CSS already gives row-major order (leading videos read across
	 * the top); this collapses the vertical whitespace a plain grid leaves
	 * under shorter cards by spanning each card across as many 10px rows as
	 * its rendered height needs.
	 */
	bindSocialMasonry() {
		if ( this.config.mode !== 'social' || ! this.resultsEl ) {
			return;
		}
		this.resultsEl.classList.add( 'is-masonry-ready' );
		this._masonryFrame = 0;
		const onResize = () => this.scheduleMasonry();
		window.addEventListener( 'resize', onResize );
		this.observeSocialImages();
		this.scheduleMasonry();
	}

	scheduleMasonry() {
		if ( this.config.mode !== 'social' || ! this.resultsEl ) {
			return;
		}
		if ( this._masonryFrame ) {
			window.cancelAnimationFrame( this._masonryFrame );
		}
		this._masonryFrame = window.requestAnimationFrame( () => {
			this._masonryFrame = 0;
			this.layoutSocialMasonry();
		} );
	}

	layoutSocialMasonry() {
		if ( ! this.resultsEl || ! this.resultsEl.classList.contains( 'is-masonry-ready' ) ) {
			return;
		}
		const styles = window.getComputedStyle( this.resultsEl );
		const rowUnit = parseFloat( styles.gridAutoRows ) || 8;
		// Vertical space between stacked cards. `is-masonry-ready` zeroes
		// `row-gap`, but `column-gap` still resolves `--crl-card-gap` to px —
		// reuse it so the span math matches the CSS grid gutter.
		const gap = parseFloat( styles.columnGap ) || 48;
		const cards = Array.from( this.resultsEl.querySelectorAll( '.campaign-resource--social' ) );
		// Don't clear existing spans before measuring: that collapses the grid to
		// one 8px row per card, the page shrinks by thousands of px mid-layout and
		// Safari (no scroll anchoring) clamps scrollY and never restores it. A
		// card's height doesn't depend on its span (`align-items: start`), so the
		// old span can stay until it is recalculated below.
		cards.forEach( ( card ) => {
			const height = card.getBoundingClientRect().height;
			if ( ! height ) {
				return;
			}
			const span = Math.max( 1, Math.ceil( ( height + gap ) / rowUnit ) );
			card.style.gridRowEnd = 'span ' + span;
		} );
	}

	observeSocialImages() {
		if ( this.config.mode !== 'social' || ! this.resultsEl ) {
			return;
		}
		this.resultsEl.querySelectorAll( 'img' ).forEach( ( img ) => {
			if ( img.dataset.crlMasonryBound ) {
				return;
			}
			img.dataset.crlMasonryBound = '1';
			if ( img.complete ) {
				return;
			}
			img.addEventListener( 'load', () => this.scheduleMasonry(), { once: true } );
			img.addEventListener( 'error', () => this.scheduleMasonry(), { once: true } );
		} );
	}

	bindCustomSelects() {
		const selects = Array.from( this.root.querySelectorAll( '[data-crl-custom-select]' ) );
		if ( ! selects.length ) {
			return;
		}

		const closeAll = () => {
			selects.forEach( ( wrapper ) => {
				wrapper.classList.remove( 'is-open' );
				const trigger = wrapper.querySelector( '.custom-select__trigger' );
				if ( trigger ) {
					trigger.setAttribute( 'aria-expanded', 'false' );
				}
			} );
		};

		const syncTriggerLabel = ( wrapper ) => {
			const native = wrapper.querySelector( 'select' );
			const trigger = wrapper.querySelector( '.custom-select__trigger' );
			if ( ! native || ! trigger ) {
				return;
			}
			const selected = native.options[ native.selectedIndex ];
			const label = selected ? selected.textContent : '';
			if ( label ) {
				trigger.textContent = label.trim();
			}
		};

		const buildOptions = ( wrapper ) => {
			const native = wrapper.querySelector( 'select' );
			const optionsEl = wrapper.querySelector( '.custom-select__options' );
			if ( ! native || ! optionsEl ) {
				return;
			}

			optionsEl.innerHTML = '';

			Array.from( native.options ).forEach( ( opt ) => {
				// keep disabled placeholder out of the list, but use it as trigger label when selected.
				if ( opt.disabled ) {
					return;
				}

				const btn = document.createElement( 'button' );
				btn.type = 'button';
				btn.className = 'custom-select__option';
				btn.setAttribute( 'role', 'option' );
				btn.setAttribute( 'data-value', opt.value );
				btn.textContent = ( opt.textContent || '' ).trim();
				btn.setAttribute( 'aria-selected', opt.selected ? 'true' : 'false' );
				if ( opt.selected ) {
					btn.classList.add( 'is-selected' );
				}

				btn.addEventListener( 'click', () => {
					native.value = opt.value;
					// Update selected flags
					Array.from( optionsEl.querySelectorAll( '.custom-select__option' ) ).forEach( ( other ) => {
						const isSelected = other === btn;
						other.classList.toggle( 'is-selected', isSelected );
						other.setAttribute( 'aria-selected', isSelected ? 'true' : 'false' );
					} );

					syncTriggerLabel( wrapper );
					wrapper.classList.remove( 'is-open' );
					const trigger = wrapper.querySelector( '.custom-select__trigger' );
					if ( trigger ) {
						trigger.setAttribute( 'aria-expanded', 'false' );
						trigger.focus();
					}

					// Fire change so existing filtering logic runs.
					native.dispatchEvent( new Event( 'change', { bubbles: true } ) );
				} );

				optionsEl.appendChild( btn );
			} );
		};

		selects.forEach( ( wrapper ) => {
			buildOptions( wrapper );
			syncTriggerLabel( wrapper );

			const native = wrapper.querySelector( 'select' );
			const trigger = wrapper.querySelector( '.custom-select__trigger' );
			if ( ! native || ! trigger ) {
				return;
			}

			// Keep native select for a11y/form submission but visually hide it.
			trigger.addEventListener( 'click', () => {
				const willOpen = ! wrapper.classList.contains( 'is-open' );
				closeAll();
				if ( willOpen ) {
					wrapper.classList.add( 'is-open' );
					trigger.setAttribute( 'aria-expanded', 'true' );
				}
			} );

			native.addEventListener( 'change', () => {
				// Selection may come from code (quick filter, reset, etc.)
				buildOptions( wrapper );
				syncTriggerLabel( wrapper );
			} );
		} );

		document.addEventListener( 'click', ( e ) => {
			const target = e.target instanceof Element ? e.target : null;
			if ( ! target ) {
				return;
			}
			if ( ! target.closest( '[data-crl-custom-select]' ) ) {
				closeAll();
			}
		} );

		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' ) {
				closeAll();
			}
		} );
	}

	// Apply the defaultMediaTab config value to pre-select the resource-type filter.
	// bindCustomSelects() already synced the trigger label from the native select's
	// PHP-rendered selected attribute; this handles cases where PHP could not set it
	// (e.g. marketing mode initial render) by setting the value and re-syncing the UI.
	syncDefaultMediaTab() {
		const tab = this.config.defaultMediaTab;
		if ( ! tab || ! this.form ) {
			return;
		}
		const select = this.form.querySelector( 'select[name="campaign-resource-type"]' );
		if ( ! select ) {
			return;
		}

		const wrapper = select.closest( '[data-crl-custom-select]' );
		const syncUi = () => {
			if ( ! wrapper ) {
				return;
			}
			const trigger = wrapper.querySelector( '.custom-select__trigger' );
			const selected = select.options[ select.selectedIndex ];
			if ( trigger && selected ) {
				trigger.textContent = ( selected.textContent || '' ).trim();
			}
			const optionsEl = wrapper.querySelector( '.custom-select__options' );
			if ( optionsEl ) {
				Array.from( optionsEl.querySelectorAll( '.custom-select__option' ) ).forEach( ( btn ) => {
					const isSelected = btn.getAttribute( 'data-value' ) === select.value;
					btn.classList.toggle( 'is-selected', isSelected );
					btn.setAttribute( 'aria-selected', isSelected ? 'true' : 'false' );
				} );
			}
		};

		if ( select.value !== tab ) {
			select.value = tab;
		}
		if ( select.value === tab ) {
			syncUi();
		}
	}

	bindForm() {
		if ( ! this.form ) {
			return;
		}
		this.form.addEventListener( 'submit', ( event ) => {
			event.preventDefault();
			this.fetchPage( 1, { replace: true } );
		} );
		this.form.querySelectorAll( 'select' ).forEach( ( select ) => {
			select.addEventListener( 'change', () => {
				if ( this.suspendSelectChange ) {
					return;
				}
				// A manual change to this field supersedes any multi-term
				// quick filter override that was carrying extra slugs for it.
				delete this.quickFilterTermOverrides[ select.name ];
				this.syncUrlFromFilters();
				this.fetchPage( 1, { replace: true } );
			} );
		} );
		this.form.querySelectorAll( 'input[type="checkbox"]' ).forEach( ( checkbox ) => {
			checkbox.addEventListener( 'change', () => {
				this.syncUrlFromFilters();
				this.fetchPage( 1, { replace: true } );
			} );
		} );
		const searchInput = this.form.querySelector( 'input[type="search"]' );
		if ( searchInput ) {
			let debounce = 0;
			searchInput.addEventListener( 'input', () => {
				window.clearTimeout( debounce );
				debounce = window.setTimeout( () => {
					this.syncUrlFromFilters();
					this.fetchPage( 1, { replace: true } );
				}, 350 );
			} );
		}
	}

	bindQuickFilters() {
		const buttons = this.root.querySelectorAll( '[data-crl-quick-filter-index]' );
		buttons.forEach( ( button ) => {
			button.addEventListener( 'click', () => {
				const idx = parseInt( button.getAttribute( 'data-crl-quick-filter-index' ) || '-1', 10 );
				const filter = ( this.config.quickFilters || [] )[ idx ];
				if ( ! filter ) {
					return;
				}
				this.applyQuickFilter( filter, button );
			} );
		} );
	}

	applyQuickFilter( filter, button ) {
		if ( ! this.form ) {
			return;
		}

		this.suspendSelectChange = true;

		const searchInput = this.form.querySelector( 'input[type="search"]' );
		if ( searchInput ) {
			searchInput.value = filter.keywords || '';
		}

		const newContentCheckbox = this.form.querySelector( 'input[name="new-content"]' );
		if ( newContentCheckbox ) {
			newContentCheckbox.checked = !! filter.newContent;
		}

		const hiddenPostTag = this.form.querySelector( 'input[name="post_tag"]' );
		if ( hiddenPostTag ) {
			hiddenPostTag.value = '';
		}

		// Reset all selects
		this.form.querySelectorAll( 'select' ).forEach( ( select ) => {
			select.value = '';
		} );

		// Group every selected slug by taxonomy so quick filters with multiple
		// terms in the same category (e.g. two Insurance types) all apply.
		this.quickFilterTermOverrides = {};
		const termsByTaxonomy = {};
		( filter.terms || [] ).forEach( ( term ) => {
			if ( ! term || ! term.taxonomy || ! term.slug ) {
				return;
			}
			if ( ! termsByTaxonomy[ term.taxonomy ] ) {
				termsByTaxonomy[ term.taxonomy ] = [];
			}
			if ( ! termsByTaxonomy[ term.taxonomy ].includes( term.slug ) ) {
				termsByTaxonomy[ term.taxonomy ].push( term.slug );
			}
		} );

		Object.keys( termsByTaxonomy ).forEach( ( taxonomy ) => {
			const slugs = termsByTaxonomy[ taxonomy ];
			let selectName = taxonomy;
			if ( taxonomy === 'social-resource-type' ) {
				selectName = 'campaign-resource-type';
			} else if ( taxonomy === 'campaign' ) {
				selectName = 'campaign-term';
			}
			if ( taxonomy === 'post_tag' ) {
				if ( hiddenPostTag ) {
					hiddenPostTag.value = slugs.join( ',' );
				}
				return;
			}
			const select = this.form.querySelector( 'select[name="' + selectName + '"]' );
			if ( select ) {
				// The select can only display one value; when there are more,
				// the full list still reaches the query via quickFilterTermOverrides.
				select.value = slugs[ 0 ];
				if ( slugs.length > 1 ) {
					this.quickFilterTermOverrides[ selectName ] = slugs.join( ',' );
				}
			}
		} );

		this.syncAllCustomSelects();

		this.root.querySelectorAll( '[data-crl-quick-filter-index]' ).forEach( ( other ) => {
			other.classList.toggle( 'is-active', other === button );
		} );

		this.suspendSelectChange = false;
		this.syncUrlFromFilters();
		this.fetchPage( 1, { replace: true } );
	}

	syncAllCustomSelects() {
		this.root.querySelectorAll( '[data-crl-custom-select] select' ).forEach( ( select ) => {
			select.dispatchEvent( new Event( 'change', { bubbles: true } ) );
		} );
	}

	applyFiltersFromUrl() {
		if ( ! this.form || ! window.URLSearchParams ) {
			return;
		}

		// phpcs:ignore WordPressVIPMinimum.JS.Window -- query params read for form state only, not HTML output.
		const params = new URLSearchParams( window.location.search );
		const keyword = params.get( 'Keyword' );
		const insuranceType = params.get( 'InsuranceType' );
		const resourceType = params.get( 'ResourceType' );
		const newContent = params.get( 'NewContent' );

		if ( null === keyword && null === insuranceType && null === resourceType && null === newContent ) {
			return;
		}

		this.suspendSelectChange = true;

		const searchInput = this.form.querySelector( 'input[type="search"]' );
		if ( searchInput && null !== keyword ) {
			searchInput.value = keyword;
		}

		const insuranceSelect = this.form.querySelector( 'select[name="insurance-type"]' );
		if ( insuranceSelect && null !== insuranceType && 'all' !== insuranceType.toLowerCase() ) {
			insuranceSelect.value = insuranceType.toLowerCase();
		}

		const resourceSelect = this.form.querySelector( 'select[name="campaign-resource-type"]' );
		if ( resourceSelect && null !== resourceType && 'all' !== resourceType.toLowerCase() ) {
			resourceSelect.value = resourceType.toLowerCase();
		}

		const newContentCheckbox = this.form.querySelector( 'input[name="new-content"]' );
		if ( newContentCheckbox && null !== newContent ) {
			newContentCheckbox.checked = '1' === newContent;
		}

		this.syncAllCustomSelects();
		this.suspendSelectChange = false;
		this.fetchPage( 1, { replace: true } );
	}

	openCart() {
		if ( ! this.cartDrawerEl ) {
			return;
		}
		window.clearTimeout( this.cartCloseTimeout );
		this.cartDrawerEl.removeAttribute( 'hidden' );
		if ( this.cartBackdropEl ) {
			this.cartBackdropEl.removeAttribute( 'hidden' );
		}
		// Removing [hidden] must land in a paint before `is-open` is added,
		// or the browser coalesces both changes and the slide-in transition
		// never runs (it "jumps" straight to the open state).
		requestAnimationFrame( () => {
			this.cartDrawerEl.classList.add( 'is-open' );
			if ( this.cartBackdropEl ) {
				this.cartBackdropEl.classList.add( 'is-open' );
			}
		} );
		if ( this.cartTriggerBtn ) {
			this.cartTriggerBtn.setAttribute( 'aria-expanded', 'true' );
		}
		// Trap focus and prevent body scroll.
		document.body.classList.add( 'crl-cart-open' );
	}

	closeCart() {
		if ( ! this.cartDrawerEl ) {
			return;
		}
		window.clearTimeout( this.cartCloseTimeout );
		this.cartDrawerEl.classList.remove( 'is-open' );
		if ( this.cartBackdropEl ) {
			this.cartBackdropEl.classList.remove( 'is-open' );
		}
		// Keep the panel in the render tree until the slide-out transition
		// (0.3s, see style.scss) finishes, then remove it from the a11y tree.
		this.cartCloseTimeout = window.setTimeout( () => {
			this.cartDrawerEl.setAttribute( 'hidden', '' );
			if ( this.cartBackdropEl ) {
				this.cartBackdropEl.setAttribute( 'hidden', '' );
			}
		}, 300 );
		if ( this.cartTriggerBtn ) {
			this.cartTriggerBtn.setAttribute( 'aria-expanded', 'false' );
		}
		document.body.classList.remove( 'crl-cart-open' );
	}

	bindCart() {
		// Campaign mode only: `cartDrawerEl` ([data-crl-cart]) doubles as the
		// always-visible "Selected Items" sidebar box in marketing mode, so
		// these open/close handlers must stay scoped behind cartTriggerBtn
		// (only rendered in campaign mode) — otherwise Escape/backdrop/close
		// would call closeCart() and permanently [hidden] the marketing
		// sidebar cart, which has no reopen trigger.
		if ( this.cartTriggerBtn ) {
			this.cartTriggerBtn.addEventListener( 'click', () => {
				const isOpen = ! this.cartDrawerEl?.hasAttribute( 'hidden' );
				if ( isOpen ) {
					this.closeCart();
				} else {
					this.openCart();
				}
			} );

			// Close on backdrop click or close button.
			if ( this.cartBackdropEl ) {
				this.cartBackdropEl.addEventListener( 'click', () => this.closeCart() );
			}
			if ( this.cartCloseBtn ) {
				this.cartCloseBtn.addEventListener( 'click', () => this.closeCart() );
			}

			// Close on Escape key.
			document.addEventListener( 'keydown', ( e ) => {
				if ( e.key === 'Escape' && this.cartDrawerEl && ! this.cartDrawerEl.hasAttribute( 'hidden' ) ) {
					this.closeCart();
				}
			} );
		}

		this.root.addEventListener( 'click', ( event ) => {
			const target = event.target instanceof Element ? event.target.closest( '[data-cr-action]' ) : null;
			if ( ! target ) {
				return;
			}
			const action = target.getAttribute( 'data-cr-action' );
			// Download button renders with this action (instead of a real
			// download href) when the viewer is logged out — see
			// campaign-resource-card.php.
			if ( action === 'login' ) {
				event.preventDefault();
				this.openLoginRequiredModal( 'Please log in to place your order.' );
				return;
			}
			if ( action === 'cart-toggle' ) {
				event.preventDefault();
				// Gate on the page-level login state (from the server-rendered
				// page, always correct) rather than the per-card
				// data-cr-requires-login flag — a REST-refreshed card can carry
				// a stale flag if the fetch was treated as anonymous.
				if ( ! this.config.isUserLoggedIn ) {
					this.openLoginRequiredModal( 'Please log in to place your order.' );
					return;
				}
				const id = parseInt( target.getAttribute( 'data-cr-id' ) || '0', 10 );
				const title = target.getAttribute( 'data-cr-title' ) || '';
				if ( id ) {
					this.toggleCartItem( id, title );
				}
			}
			if ( action === 'design-huddle' ) {
				event.preventDefault();
				const templateId = target.getAttribute( 'data-cr-design-huddle-template-id' ) || '';
				this.openDesignHuddle( templateId );
			}
		} );

		if ( this.cartOrderBtn ) {
			this.cartOrderBtn.addEventListener( 'click', () => {
				if ( ! this.cart.size ) {
					return;
				}
				const ids = Array.from( this.cart.keys() );
				const sep = this.config.orderUrl.indexOf( '?' ) === -1 ? '?' : '&';
				const url = this.config.orderUrl + sep + encodeURIComponent( this.config.orderQueryArg ) + '=' + ids.join( ',' );
				window.location.assign( url );
			} );
		}

		this.root.addEventListener( 'click', ( event ) => {
			const removeBtn = event.target instanceof Element ? event.target.closest( '[data-crl-cart-remove]' ) : null;
			if ( removeBtn ) {
				event.preventDefault();
				const id = parseInt( removeBtn.getAttribute( 'data-crl-cart-remove' ) || '0', 10 );
				if ( id ) {
					this.cart.delete( id );
					persistCart( this.config.mode, this.cart );
					this.renderCart();
					this.syncCardSelectedState();
				}
			}
		} );

		if ( this.loadMoreBtn ) {
			this.loadMoreBtn.addEventListener( 'click', () => {
				if ( this.isFetching || this.currentPage >= this.maxPages ) {
					return;
				}
				this.setLoadMoreBusy( true );
				this.fetchPage( this.currentPage + 1, { replace: false } );
			} );
		}
	}

	toggleCartItem( id, title ) {
		if ( this.cart.has( id ) ) {
			this.cart.delete( id );
		} else {
			this.cart.set( id, title );
		}
		persistCart( this.config.mode, this.cart );
		this.renderCart();
		this.syncCardSelectedState();
	}

	syncCardSelectedState() {
		const cards = this.root.querySelectorAll( '.campaign-resource' );
		cards.forEach( ( card ) => {
			const id = parseInt( card.getAttribute( 'data-id' ) || '0', 10 );
			const isSelected = this.cart.has( id );
			card.classList.toggle( 'is-selected', isSelected );
			const btn = card.querySelector( '[data-cr-action="cart-toggle"]' );
			if ( btn ) {
				btn.setAttribute( 'aria-pressed', isSelected ? 'true' : 'false' );
				btn.classList.toggle( 'is-selected', isSelected );
			}
		} );
	}

	renderCart() {
		const countText = '(' + this.cart.size + ')';
		this.cartCountEls.forEach( ( el ) => {
			el.textContent = countText;
		} );
		if ( ! this.cartItemsEl ) {
			return;
		}
		while ( this.cartItemsEl.firstChild ) {
			this.cartItemsEl.removeChild( this.cartItemsEl.firstChild );
		}
		this.cart.forEach( ( title, id ) => {
			const li = document.createElement( 'li' );
			li.className = 'campaign-resource-list__cart-item';
			li.setAttribute( 'data-id', String( id ) );

			const remove = document.createElement( 'button' );
			remove.type = 'button';
			remove.className = 'campaign-resource-list__cart-remove';
			remove.setAttribute( 'data-crl-cart-remove', String( id ) );
			remove.setAttribute( 'aria-label', 'Remove ' + title );
			remove.textContent = 'x';

			const span = document.createElement( 'span' );
			span.className = 'campaign-resource-list__cart-item-title';
			span.textContent = title;

			li.appendChild( span );
			li.appendChild( remove );
			this.cartItemsEl.appendChild( li );
		} );
	}

	bindPreviewModal() {
		if ( ! this.previewModalEl ) {
			return;
		}

		// Event delegation — works for AJAX-loaded cards too.
		this.root.addEventListener( 'click', ( event ) => {
			const trigger = event.target instanceof Element
				? event.target.closest( '[data-crl-preview-trigger]' )
				: null;
			if ( trigger ) {
				event.preventDefault();
				this.openPreviewModal( trigger );
			}
		} );

		this.previewModalEl.querySelectorAll( '[data-crl-modal-close]' ).forEach( ( el ) => {
			el.addEventListener( 'click', () => this.closePreviewModal() );
		} );
		// Also close on overlay click (content-calendar-modal pattern).
		const overlay = this.previewModalEl.querySelector( '.crl-preview-modal__overlay' );
		if ( overlay ) {
			overlay.addEventListener( 'click', () => this.closePreviewModal() );
		}

		// Copy text to clipboard.
		if ( this.previewModalCopyBtn ) {
			this.previewModalCopyBtn.addEventListener( 'click', () => {
				const text = this.previewModalPostCopy
					? this.previewModalPostCopy.textContent || ''
					: '';
				if ( text && navigator.clipboard ) {
					navigator.clipboard.writeText( text ).then( () => {
						const originalText = this.previewModalCopyBtn.textContent;
						this.previewModalCopyBtn.textContent = 'Copied!';
						window.setTimeout( () => {
							this.previewModalCopyBtn.textContent = originalText;
						}, 1800 );
					} ).catch( () => {} );
				}
			} );
		}

		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && this.previewModalEl && ! this.previewModalEl.hasAttribute( 'hidden' ) ) {
				this.closePreviewModal();
			}
		} );
	}

	openInitialSocialResource() {
		const id = parseInt( this.config.socialResourceId || 0, 10 );
		if ( this.config.mode !== 'social' || ! id || ! this.previewModalEl ) {
			return;
		}
		window.setTimeout( () => {
			const trigger = this.root.querySelector( '[data-crl-preview-trigger][data-crl-resource-id="' + String( id ) + '"]' );
			if ( trigger ) {
				this.openPreviewModal( trigger );
			}
		}, 0 );
	}

	openPreviewModal( trigger ) {
		if ( ! this.previewModalEl ) {
			return;
		}

		const assetUrl    = trigger.getAttribute( 'data-crl-asset-url' ) || '';
		const assetType   = trigger.getAttribute( 'data-crl-asset-type' ) || 'image';
		const title       = trigger.getAttribute( 'data-crl-preview-title' ) || '';
		const isSocial    = this.config.mode === 'social';
		const postCopy    = trigger.getAttribute( 'data-crl-post-copy' ) || '';
		const resourceType = trigger.getAttribute( 'data-crl-resource-type' ) || '';
		const shareXUrl   = trigger.getAttribute( 'data-crl-share-x-url' ) || '';
		const shareFbUrl  = trigger.getAttribute( 'data-crl-share-fb-url' ) || '';
		const downloadUrl = trigger.getAttribute( 'data-crl-download-url' ) || '';
		const resourceUrl = trigger.getAttribute( 'data-crl-resource-url' ) || '';
		const designHuddleTemplateId = trigger.getAttribute( 'data-crl-design-huddle-template-id' ) || '';

		this.lastPreviewFocused = trigger instanceof HTMLElement ? trigger : null;

		if ( isSocial && ! this.config.isUserLoggedIn ) {
			this.openLoginRequiredModal();
			return;
		}

		// Media — image or video.
		const mediaUrl = assetUrl || downloadUrl;
		if ( this.previewModalImg ) {
			// Swap in a video element for video assets, keep img for everything else.
			const mediaContainer = this.previewModalImg.parentElement;
			if ( assetType === 'video' && mediaUrl ) {
				// Replace static <img> with a <video> for this open.
				const video = document.createElement( 'video' );
				video.src = mediaUrl;
				video.controls = true;
				video.autoplay = true;
				video.className = 'crl-preview-modal__video';
				this.previewModalImg.setAttribute( 'hidden', '' );
				// Remove any existing video first.
				const prevVideo = mediaContainer ? mediaContainer.querySelector( 'video' ) : null;
				if ( prevVideo ) {
					prevVideo.pause();
					prevVideo.remove();
				}
				if ( mediaContainer ) {
					mediaContainer.appendChild( video );
				}
			} else {
				// Show image.
				const prevVideo = mediaContainer ? mediaContainer.querySelector( 'video' ) : null;
				if ( prevVideo ) {
					prevVideo.pause();
					prevVideo.remove();
				}
				this.previewModalImg.src = mediaUrl || '';
				this.previewModalImg.alt = title || '';
				this.previewModalImg.toggleAttribute( 'hidden', ! mediaUrl );
			}
		}

		if ( isSocial ) {
			// Populate social body panel (content-calendar style).
			if ( this.previewModalTitle ) {
				this.previewModalTitle.textContent = title;
			}
			if ( this.previewModalPostCopy ) {
				this.previewModalPostCopy.textContent = postCopy;
			}
			if ( this.previewModalType ) {
				this.previewModalType.textContent = resourceType;
			}
			if ( this.previewModalShareX ) {
				this.previewModalShareX.href = shareXUrl || '#';
			}
			if ( this.previewModalShareFb ) {
				this.previewModalShareFb.href = shareFbUrl || '#';
			}
			if ( this.previewModalDownload ) {
				this.previewModalDownload.href = downloadUrl || '#';
				this.previewModalDownload.setAttribute( 'aria-disabled', downloadUrl ? 'false' : 'true' );
				this.previewModalDownload.classList.toggle( 'is-disabled', ! downloadUrl );
			}
			if ( this.previewModalResourceLink ) {
				this.previewModalResourceLink.href = resourceUrl || '#';
				this.previewModalResourceLink.textContent = resourceUrl || '';
			}
			if ( this.previewModalDesignHuddle ) {
				if ( designHuddleTemplateId && this.config.designHuddlePageUrl ) {
					const sep = this.config.designHuddlePageUrl.indexOf( '?' ) === -1 ? '?' : '&';
					this.previewModalDesignHuddle.href = this.config.designHuddlePageUrl + sep + 'template_id=' + encodeURIComponent( designHuddleTemplateId );
					this.previewModalDesignHuddle.removeAttribute( 'hidden' );
				} else {
					this.previewModalDesignHuddle.setAttribute( 'hidden', '' );
				}
			}
			if ( this.previewModalBody ) {
				this.previewModalBody.removeAttribute( 'hidden' );
			}
			// Social: media + copy panel share the two-column layout.
			this.previewModalEl.classList.remove( 'crl-preview-modal--media-only' );
		} else {
			// Non-social: simple title below the image.
			if ( this.previewModalTitleSimple ) {
				this.previewModalTitleSimple.textContent = title;
			}
			if ( this.previewModalBody ) {
				this.previewModalBody.setAttribute( 'hidden', '' );
			}
			// Campaign / marketing: no copy panel — let the asset use the full
			// panel width (Trusted Choice `modal--one-col`), not half.
			this.previewModalEl.classList.add( 'crl-preview-modal--media-only' );
		}

		this.previewModalEl.removeAttribute( 'hidden' );
		document.body.classList.add( 'crl-modal-open' );
		this.blurPageFocus();
	}

	// Opens the standalone Design Huddle editor page in a new tab. Mirrors
	// trusted-choice's window.openDesignHuddle(), but the base URL comes
	// from a server-localized value (this.config.designHuddlePageUrl)
	// rather than window.location.hostname.
	openDesignHuddle( templateId ) {
		if ( ! templateId || ! this.config.designHuddlePageUrl ) {
			return;
		}
		const sep = this.config.designHuddlePageUrl.indexOf( '?' ) === -1 ? '?' : '&';
		const url = this.config.designHuddlePageUrl + sep + 'template_id=' + encodeURIComponent( templateId );
		window.open( url, '_blank', 'noopener' );
	}

	openLoginRequiredModal( messageText ) {
		if ( ! this.previewModalEl ) {
			return;
		}
		this.clearLoginRequiredMessage();
		const panel = this.previewModalEl.querySelector( '.crl-preview-modal__panel' );
		const layout = this.previewModalEl.querySelector( '.crl-preview-modal__layout' );
		const titleSimple = this.previewModalEl.querySelector( '[data-crl-modal-title-simple]' );
		const message = document.createElement( 'h2' );
		message.className = 'crl-preview-modal__login-required';
		message.textContent = messageText || 'Please log in to view.';
		if ( layout ) {
			layout.setAttribute( 'hidden', '' );
		}
		if ( titleSimple ) {
			titleSimple.setAttribute( 'hidden', '' );
		}
		if ( panel ) {
			panel.appendChild( message );
		}
		this.previewModalEl.removeAttribute( 'hidden' );
		document.body.classList.add( 'crl-modal-open' );
		this.blurPageFocus();
	}

	blurPageFocus() {
		if ( document.activeElement instanceof HTMLElement ) {
			document.activeElement.blur();
		}
	}

	clearLoginRequiredMessage() {
		const message = this.previewModalEl.querySelector( '.crl-preview-modal__login-required' );
		if ( message ) {
			message.remove();
		}
		const layout = this.previewModalEl.querySelector( '.crl-preview-modal__layout' );
		const titleSimple = this.previewModalEl.querySelector( '[data-crl-modal-title-simple]' );
		if ( layout ) {
			layout.removeAttribute( 'hidden' );
		}
		if ( titleSimple ) {
			titleSimple.removeAttribute( 'hidden' );
		}
	}

	closePreviewModal() {
		if ( ! this.previewModalEl ) {
			return;
		}

		// Stop + remove any dynamically added video.
		const video = this.previewModalEl.querySelector( 'video' );
		if ( video ) {
			video.pause();
			video.remove();
		}

		// Reset static image.
		if ( this.previewModalImg ) {
			this.previewModalImg.src = '';
			this.previewModalImg.removeAttribute( 'hidden' );
		}

		// Hide social body.
		if ( this.previewModalBody ) {
			this.previewModalBody.setAttribute( 'hidden', '' );
		}
		this.previewModalEl.classList.remove( 'crl-preview-modal--media-only' );
		this.clearLoginRequiredMessage();

		this.previewModalEl.setAttribute( 'hidden', '' );
		document.body.classList.remove( 'crl-modal-open' );

		if ( this.lastPreviewFocused instanceof HTMLElement ) {
			this.lastPreviewFocused.focus();
		}
	}

	collectFilterParams() {
		const params = {
			mode: this.config.mode,
			per_page: this.config.perPage,
		};

		if ( ! this.form ) {
			return params;
		}
		const formData = new FormData( this.form );
		formData.forEach( ( rawValue, name ) => {
			const value = typeof rawValue === 'string' ? rawValue.trim() : '';
			if ( '' === value ) {
				return;
			}
			params[ name ] = value;
		} );

		// A multi-term quick filter carries more slugs for a field than its
		// single-value <select> can display; send the full list instead.
		Object.keys( this.quickFilterTermOverrides ).forEach( ( name ) => {
			params[ name ] = this.quickFilterTermOverrides[ name ];
		} );

		// On campaign mode, force the active campaign term server-side to the page's campaign.
		if ( this.config.mode === 'campaign' && this.config.campaignTerm ) {
			params[ 'campaign-term' ] = this.config.campaignTerm;
		}

		// Social mode: pass configured post types to REST endpoint.
		if ( this.config.mode === 'social' && this.config.socialPostTypes ) {
			params[ 'social_post_types' ] = this.config.socialPostTypes;
		}

		return params;
	}

	syncUrlFromFilters() {
		if ( this.config.mode !== 'social' || ! this.form || ! window.history || ! window.URLSearchParams ) {
			return;
		}

		const params = new URLSearchParams();
		const searchInput = this.form.querySelector( 'input[type="search"]' );
		const keyword = searchInput ? searchInput.value.trim() : '';
		params.set( 'Keyword', keyword );

		const insuranceSelect = this.form.querySelector( 'select[name="insurance-type"]' );
		params.set( 'InsuranceType', insuranceSelect && insuranceSelect.value ? insuranceSelect.value : 'all' );

		const resourceSelect = this.form.querySelector( 'select[name="campaign-resource-type"]' );
		params.set( 'ResourceType', resourceSelect && resourceSelect.value ? resourceSelect.value : 'all' );

		params.set( 'CampaignType', 'all' );
		params.set( 'Region', 'all' );

		const newContent = this.form.querySelector( 'input[name="new-content"]' );
		params.set( 'NewContent', newContent && newContent.checked ? '1' : 'all' );

		// phpcs:ignore WordPressVIPMinimum.JS.Window -- preserve deep-link id when syncing filter URL.
		const socialResourceId = new URLSearchParams( window.location.search ).get( 'SocialResourceId' );
		if ( socialResourceId ) {
			params.set( 'SocialResourceId', socialResourceId );
		}

		window.history.pushState( null, '', '?' + params.toString() );
	}

	setLoadMoreBusy( isBusy ) {
		if ( ! this.loadMoreBtn ) {
			return;
		}
		this.loadMoreBtn.classList.toggle( 'is-loading', !! isBusy );
		this.loadMoreBtn.setAttribute( 'aria-busy', isBusy ? 'true' : 'false' );
		if ( isBusy ) {
			this.loadMoreBtn.disabled = true;
		}
	}

	fetchPage( pageNumber, { replace } ) {
		if ( ! this.resultsEl || this.isFetching ) {
			return;
		}

		const filters = this.collectFilterParams();
		filters.paged = Math.max( 1, parseInt( pageNumber, 10 ) || 1 );

		const url = appendQueryArgs( this.config.restUrl, filters );

		this.isFetching = true;
		this.setLoadMoreBusy( true );
		this.resultsEl.classList.add( 'is-loading' );

		// X-WP-Nonce lets the REST endpoint see the logged-in cookie, so
		// `is_user_logged_in()` in serve() matches the page — otherwise
		// REST-loaded cards render gated ("log in to add to cart").
		const headers = { Accept: 'application/json' };
		if ( this.config.restNonce ) {
			headers[ 'X-WP-Nonce' ] = this.config.restNonce;
		}

		fetch( url, {
			credentials: 'same-origin',
			headers,
		} )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw new Error( 'Request failed' );
				}
				return response.json();
			} )
			.then( ( data ) => {
				this.handleResponse( data, { replace } );
			} )
			.catch( () => {
				this.resultsEl.classList.remove( 'is-loading' );
			} )
			.finally( () => {
				this.isFetching = false;
				this.setLoadMoreBusy( false );
				this.updatePagination();
			} );
	}

	handleResponse( data, { replace } ) {
		if ( ! data || ! Array.isArray( data.items ) ) {
			this.resultsEl.classList.remove( 'is-loading' );
			return;
		}

		const html = data.items.map( ( item ) => item.html || '' ).join( '' );

		if ( replace ) {
			while ( this.resultsEl.firstChild ) {
				this.resultsEl.removeChild( this.resultsEl.firstChild );
			}
			if ( ! html ) {
				const emptyMessage = this.resultsEl.getAttribute( 'data-crl-empty' ) || 'No resources match your search';
				const emptyParagraph = document.createElement( 'p' );
				emptyParagraph.className = 'campaign-resource-list__empty';
				emptyParagraph.textContent = emptyMessage;
				this.resultsEl.appendChild( emptyParagraph );
			} else {
				appendHtmlNodes( this.resultsEl, html );
			}
		} else {
			appendHtmlNodes( this.resultsEl, html );
		}

		this.currentPage = data.current_page || 1;
		this.maxPages = data.max_num_pages || 1;
		this.foundPosts = data.found_posts || 0;

		this.resultsEl.classList.remove( 'is-loading' );
		this.syncCardSelectedState();
		this.observeSocialImages();
		this.scheduleMasonry();
	}

	updatePagination() {
		if ( ! this.paginationEl ) {
			return;
		}

		// Marketing and social modes use Load More (not numeric pagination).
		if ( this.config.mode === 'marketing' || this.config.mode === 'social' ) {
			if ( this.loadMoreBtn ) {
				const hasMore = this.currentPage < this.maxPages;
				this.paginationEl.toggleAttribute( 'hidden', ! hasMore );
				this.loadMoreBtn.disabled = this.isFetching || ! hasMore;
				if ( ! this.isFetching ) {
					this.setLoadMoreBusy( false );
				}
			}
			return;
		}

		// Campaign mode: numeric pagination
		if ( ! this.pageNavEl ) {
			return;
		}
		this.paginationEl.toggleAttribute( 'hidden', this.maxPages <= 1 );
		while ( this.pageNavEl.firstChild ) {
			this.pageNavEl.removeChild( this.pageNavEl.firstChild );
		}

		const prevLabel = this.config.paginationPrev || '\u2190 Previous';
		const nextLabel = this.config.paginationNext || 'Next \u2192';

		const prevBtn = document.createElement( 'button' );
		prevBtn.type = 'button';
		prevBtn.className = 'campaign-resource-list__page-nav-prev';
		prevBtn.textContent = prevLabel;
		prevBtn.disabled = this.currentPage <= 1;
		prevBtn.addEventListener( 'click', () => {
			if ( this.currentPage > 1 ) {
				this.fetchPage( this.currentPage - 1, { replace: true } );
			}
		} );

		const numbersWrap = document.createElement( 'div' );
		numbersWrap.className = 'campaign-resource-list__page-nav-numbers';

		for ( let i = 1; i <= this.maxPages; i += 1 ) {
			const button = document.createElement( 'button' );
			button.type = 'button';
			button.className = 'campaign-resource-list__page-nav-item';
			if ( i === this.currentPage ) {
				button.classList.add( 'is-current' );
				button.setAttribute( 'aria-current', 'page' );
			}
			button.textContent = String( i );
			button.addEventListener( 'click', () => this.fetchPage( i, { replace: true } ) );
			numbersWrap.appendChild( button );
		}

		const nextBtn = document.createElement( 'button' );
		nextBtn.type = 'button';
		nextBtn.className = 'campaign-resource-list__page-nav-next';
		nextBtn.textContent = nextLabel;
		nextBtn.disabled = this.currentPage >= this.maxPages;
		nextBtn.addEventListener( 'click', () => {
			if ( this.currentPage < this.maxPages ) {
				this.fetchPage( this.currentPage + 1, { replace: true } );
			}
		} );

		this.pageNavEl.appendChild( prevBtn );
		this.pageNavEl.appendChild( numbersWrap );
		this.pageNavEl.appendChild( nextBtn );
	}
}

function parseConfig( root ) {
	const raw = root.getAttribute( 'data-crl-config' );
	if ( ! raw ) {
		return null;
	}
	try {
		return JSON.parse( raw );
	} catch ( error ) {
		return null;
	}
}

function appendQueryArgs( url, params ) {
	const search = new URLSearchParams();
	Object.keys( params ).forEach( ( key ) => {
		search.set( key, String( params[ key ] ) );
	} );
	return url + ( url.indexOf( '?' ) === -1 ? '?' : '&' ) + search.toString();
}

/**
 * Parse a server-rendered HTML string and append the parsed nodes to a target.
 * Uses a `<template>` element so the markup is parsed but not executed; nodes
 * are then moved into the target via `appendChild` (DOM construction, no HTML
 * concatenation).
 */
function appendHtmlNodes( target, html ) {
	if ( ! target || ! html ) {
		return;
	}
	const tpl = document.createElement( 'template' );
	// phpcs:ignore WordPressVIPMinimum.JS.InnerHTML.Found -- Server-rendered card HTML is escaped in PHP before the REST response.
	tpl.innerHTML = html;
	const fragment = tpl.content;
	while ( fragment.firstChild ) {
		target.appendChild( fragment.firstChild );
	}
}

function restoreCart( mode ) {
	const cart = new Map();
	try {
		const stored = window.sessionStorage.getItem( STORAGE_KEY_PREFIX + mode );
		if ( ! stored ) {
			return cart;
		}
		const parsed = JSON.parse( stored );
		if ( Array.isArray( parsed ) ) {
			parsed.forEach( ( entry ) => {
				if ( entry && typeof entry.id === 'number' && typeof entry.title === 'string' ) {
					cart.set( entry.id, entry.title );
				}
			} );
		}
	} catch ( error ) {
		// ignore corrupt storage
	}
	return cart;
}

function persistCart( mode, cart ) {
	try {
		const list = [];
		cart.forEach( ( title, id ) => list.push( { id, title } ) );
		window.sessionStorage.setItem( STORAGE_KEY_PREFIX + mode, JSON.stringify( list ) );
	} catch ( error ) {
		// ignore quota errors
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.campaign-resource-list[data-crl-config]' ).forEach( ( root ) => {
		new CampaignResourceListController( root );
	} );
} );
